import React from "react";
import { motion } from "framer-motion";
import { MapPin, Navigation } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { useLang } from "@/context/LanguageContext";

export const Location = () => {
  const { content } = useContent();
  const { t, L } = useLang();
  const loc = content?.location || {};

  return (
    <section id="location" className="bg-ocean-900 py-20 sm:py-28 text-sand-100" data-testid="location-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 grid lg:grid-cols-2 gap-10 items-stretch">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="flex flex-col justify-center"
        >
          <p className="eyebrow text-gold mb-4">{t("location.eyebrow")}</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight leading-[1.05] mb-4">
            {L(loc.name)}
          </h2>
          <p className="flex items-start gap-2 text-sand-100/70 text-lg mb-8">
            <MapPin className="w-5 h-5 mt-1 text-gold shrink-0" /> {L(loc.address)}
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {(loc.info || []).map((it, i) => (
              <div key={i} data-testid={`location-info-${i}`} className="bg-ocean-800 rounded-xl p-4 border border-white/10">
                <div className="font-mono text-[0.6rem] tracking-[0.18em] uppercase text-gold mb-1">{L(it.label)}</div>
                <div className="font-heading font-bold">{L(it.value)}</div>
              </div>
            ))}
          </div>

          <a
            href={loc.directions_url || "#"}
            target="_blank"
            rel="noreferrer"
            data-testid="get-directions-button"
            className="inline-flex items-center justify-center gap-2 bg-coral hover:bg-coral/90 text-white font-heading font-bold px-8 py-4 rounded-full transition-all hover:scale-[1.03] w-full sm:w-auto"
          >
            <Navigation className="w-5 h-5" /> {t("location.directions")}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl overflow-hidden border border-white/10 min-h-[320px] lg:min-h-full"
        >
          <iframe
            title="Mappa Salty Folks - Isola delle Femmine"
            src={loc.maps_embed}
            className="w-full h-full min-h-[320px]"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="location-map"
          />
        </motion.div>
      </div>
    </section>
  );
};
